import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Cloud, Crown, RotateCcw, ScanLine, Sparkles, Tag, Users } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Btn, Page, Spinner, TopBar } from "@/components/kit";
import { usePaymentConfig, useSubscription } from "@/hooks/queries";
import { api } from "@/lib/api";
import { periodLabel, rupees } from "@/lib/toto";

const BENEFITS = [
  [ScanLine, "Unlimited visiting-card scans"], [Sparkles, "AI-powered extraction"], [Users, "Contact storage"],
  [Cloud, "Card image storage"], [RotateCcw, "Device contact saving"], [Tag, "Automatic categorization"], [Cloud, "Secure cloud backup"],
];
const SRC = "https://checkout.razorpay.com/v1/checkout.js";
const loadScript = () => new Promise((res) => {
  if (window.Razorpay) return res(true);
  const s = document.createElement("script");
  s.src = SRC;
  s.onload = () => res(true);
  s.onerror = () => res(false);
  document.body.appendChild(s);
});

function PayButton({ label, onSuccess }) {
  const [loading, setLoading] = useState(false);
  useEffect(() => { loadScript(); }, []);
  const pay = useCallback(async () => {
    setLoading(true);
    try {
      if (!(await loadScript()) || !window.Razorpay) throw new Error("Could not load payment gateway.");
      const o = await api.post("/payments/create-order");
      const rz = new window.Razorpay({
        key: o.keyId, amount: o.amount, currency: o.currency, name: o.name, description: o.description, order_id: o.orderId, prefill: o.prefill,
        theme: { color: "#0000EC" },
        handler: async (r) => {
          try {
            await api.post("/payments/verify", { razorpay_order_id: r.razorpay_order_id, razorpay_payment_id: r.razorpay_payment_id, razorpay_signature: r.razorpay_signature });
            onSuccess();
          } catch { toast.error("We couldn't verify your payment. Please contact support."); }
          finally { setLoading(false); }
        },
        modal: { ondismiss: () => setLoading(false) },
      });
      rz.on("payment.failed", () => { toast.error("Payment failed. Please try again."); setLoading(false); });
      rz.open();
    } catch (e) {
      toast.error(e.message || "Could not start payment.");
      setLoading(false);
    }
  }, [onSuccess]);
  return <Btn className="w-full" onClick={pay} loading={loading} testid="pay-button">{label}</Btn>;
}

export default function Subscription() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const sub = useSubscription();
  const cfg = usePaymentConfig().data;
  const price = cfg ? `${rupees(cfg.amount)}/${periodLabel(cfg.durationDays)}` : "₹199/year";
  const isPremium = sub.data?.planId === "premium" && sub.data?.status === "active";
  const expiry = sub.data?.expiryDate ? new Date(sub.data.expiryDate).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null;
  const onSuccess = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["subscription"] });
    qc.invalidateQueries({ queryKey: ["scanUsage"] });
    toast.success("Welcome to Premium! Enjoy unlimited scans.");
    nav("/");
  }, [qc, nav]);

  return (
    <Page>
      <TopBar title="TOTO Premium" />
      {sub.isLoading ? <Spinner /> : (
        <div className="max-w-xl mx-auto">
          <div className="rounded-[24px] p-6 text-white bg-gradient-to-br from-[#4F46E5] to-[#7C3AED] relative overflow-hidden mt-2">
            <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/10" />
            <Crown className="w-10 h-10" />
            <h1 className="text-[28px] font-extrabold mt-3">TOTO Premium</h1>
            <p className="text-[32px] font-extrabold mt-2" data-testid="subscription-price">{price}</p>
            {isPremium && <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-[13px] font-bold" data-testid="subscription-status"><Check className="w-4 h-4" />Active{expiry && !sub.data?.isAdminEntitlement ? ` · until ${expiry}` : ""}</p>}
          </div>
          <ul className="t-card p-5 mt-5 space-y-3.5">
            {BENEFITS.map(([I, l]) => <li key={l} className="flex items-center gap-3 text-[15px] font-medium"><span className="w-9 h-9 rounded-[10px] bg-[#F5F3FF] flex items-center justify-center"><I className="w-5 h-5 text-[#7C3AED]" /></span>{l}</li>)}
          </ul>
          <div className="mt-6">
            {isPremium ? <p className="text-center text-[15px] font-semibold text-[#059669]" data-testid="subscription-active-note">You're on Premium. Enjoy unlimited scans!</p>
              : cfg && !cfg.enabled ? <p className="text-center text-[15px] text-[#64748B]" data-testid="subscription-disabled">Premium purchases are temporarily unavailable.</p>
              : <PayButton label={`Upgrade for ${price}`} onSuccess={onSuccess} />}
            <p className="mt-3 text-center text-[12px] text-[#64748B]">Secure payment via Razorpay. One-time payment, no auto-renewal.</p>
          </div>
        </div>
      )}
    </Page>
  );
}
